import { useEffect, useState, useRef } from 'react';

export type ProjectUpdateEvent = {
    percent?: number;
    status?: string;
    score_data?: any;
    error?: string;
    progress?: number;
    event?: string;
};

export const useProjectUpdates = (projectId: string | null) => {
    const [lastUpdate, setLastUpdate] = useState<ProjectUpdateEvent | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const eventSourceRef = useRef<EventSource | null>(null);

    useEffect(() => {
        if (!projectId) {
            return;
        }

        // Close existing connection if any
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
        const url = `${apiUrl}/api/v1/sse/project/${projectId}`;

        console.log(`Connecting to SSE: ${url}`);

        // Use withCredentials to send cookies if auth relies on them
        const eventSource = new EventSource(url, { withCredentials: true });
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
            console.log("SSE Connection opened");
            setIsConnected(true);
            setError(null);
        };

        eventSource.onmessage = (event) => {
            // Heartbeats or generic messages
            console.log("SSE Message:", event.data);
        };

        eventSource.addEventListener("connected", (event) => {
            console.log("SSE Connected event:", event.data);
        });

        eventSource.addEventListener("update", (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log("SSE Update received:", data);
                setLastUpdate(data);

                if (data.status === 'failed' || data.error) {
                    setError(data.error || 'Unknown error');
                }
            } catch (e) {
                console.error("Error parsing SSE data", e);
            }
        });

        eventSource.addEventListener("error", (event: any) => {
            console.error("SSE Error:", event);
            // If data is present, it might be a server-sent error event
            if (event.data) {
                setError(event.data);
            } else {
                // Connection error
                // EventSource automatically retries, but we update state
                setIsConnected(false);
            }
        });

        eventSource.onerror = (err) => {
            console.error("SSE Connection Error:", err);
            setIsConnected(false);
            // EventSource will retry automatically
        };

        return () => {
            console.log("Closing SSE connection");
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            setIsConnected(false);
        };
    }, [projectId]);

    return { lastUpdate, isConnected, error };
};
